import React from "react";
import { User, Bot, Loader2 } from "lucide-react";
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface MessageBubbleProps {
  role: 'user' | 'assistant';
  content: string;
  metadata?: any;
  isStreaming?: boolean;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ role, content, metadata, isStreaming = false }) => {
  return (
    <div className={`flex ${role === "user" ? "justify-end" : "justify-start"}`}>
      <div
        className={`flex gap-3 max-w-[85%] ${
          role === "user" ? "flex-row-reverse" : "flex-row"
        }`}
      >
        <div
          className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${
            role === "user" 
              ? "bg-primary text-primary-foreground" 
              : "bg-secondary text-secondary-foreground"
          }`}
        >
          {role === "user" ? (
            <User className="h-4 w-4" />
          ) : (
            <Bot className="h-4 w-4" />
          )}
        </div>
        
        <div
          className={`p-4 rounded-lg ${
            role === "user"
              ? "bg-primary text-primary-foreground"
              : "bg-secondary text-secondary-foreground"
          }`}
        >
          {role === "assistant" ? (
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <ReactMarkdown
                components={{
                  code(props) {
                    const { node, className, children, ...rest } = props;
                    const match = /language-(\w+)/.exec(className || '');
                    const isInline = !match;
                    
                    return !isInline && match ? (
                      <SyntaxHighlighter
                        style={oneDark}
                        language={match[1]}
                        PreTag="div"
                        customStyle={{
                          borderRadius: '0.375rem',
                          padding: '1rem',
                          margin: '0.75rem 0',
                        }}
                      >
                        {String(children).replace(/\n$/, '')}
                      </SyntaxHighlighter>
                    ) : (
                      <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono" {...rest}>
                        {children}
                      </code>
                    );
                  },
                  h1: ({node, ...props}) => (
                    <h1 className="text-2xl font-bold mt-6 mb-4 text-foreground" {...props} />
                  ),
                  h2: ({node, ...props}) => (
                    <h2 className="text-xl font-semibold mt-5 mb-3 text-foreground" {...props} />
                  ),
                  h3: ({node, ...props}) => (
                    <h3 className="text-lg font-semibold mt-4 mb-2 text-foreground" {...props} />
                  ),
                  ul: ({node, ...props}) => (
                    <ul className="list-disc ml-6 my-3 space-y-1.5" {...props} />
                  ),
                  ol: ({node, ...props}) => (
                    <ol className="list-decimal ml-6 my-3 space-y-1.5" {...props} />
                  ),
                  li: ({node, ...props}) => (
                    <li className="text-foreground leading-relaxed" {...props} />
                  ),
                  blockquote: ({node, ...props}) => (
                    <blockquote className="border-l-4 border-primary pl-4 my-4 italic bg-muted/30 py-2 rounded-r" {...props} />
                  ),
                  p: ({node, ...props}) => (
                    <p className="mb-3 leading-relaxed text-foreground" {...props} />
                  ),
                  table: ({node, ...props}) => (
                    <div className="overflow-x-auto my-4">
                      <table className="min-w-full border-collapse border border-border rounded" {...props} />
                    </div>
                  ),
                  th: ({node, ...props}) => (
                    <th className="border border-border bg-muted px-4 py-2 text-left font-semibold" {...props} />
                  ),
                  td: ({node, ...props}) => (
                    <td className="border border-border px-4 py-2" {...props} />
                  ),
                  hr: ({node, ...props}) => (
                    <hr className="my-6 border-border" {...props} />
                  ),
                }}
              >
                {content}
              </ReactMarkdown>
              {isStreaming && (
                <div className="flex items-center gap-2 text-muted-foreground mt-2">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  <span className="text-xs">AI is typing...</span>
                </div>
              )}
            </div>
          ) : (
            <div className="whitespace-pre-wrap">{content}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessageBubble;
